import { Currency } from '@/constants';

const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_HOUR = 60;
const MILLISECONDS_PER_SECONDS = 1000;

/**
 * converts the given seconds to a duration timestamp in the format hh:mm:ss
 * NOTE only works for times less than 1 day!
 */
export const convertSecondsToHoursMinutes = (
  seconds: number,
  longForm = false,
) => {
  const durationInMinutes = Math.round(seconds / SECONDS_PER_MINUTE);

  const hour = Math.floor(durationInMinutes / MINUTES_PER_HOUR);
  const minute = durationInMinutes % MINUTES_PER_HOUR;

  let hDisplay;
  let mDisplay;
  if (longForm) {
    hDisplay = hour > 0 ? `${hour} hr${hour > 1 ? 's' : ''} ` : '';
    mDisplay = minute > 0 ? `${minute} min${minute > 1 ? 's' : ''}` : '';
  } else {
    hDisplay = hour > 0 ? `${hour}h ` : '';
    mDisplay = minute > 0 ? `${minute}m` : '';
  }

  return hDisplay + mDisplay;
};

/**
 * converts the given milliseconds to a duration timestamp in the format hh:mm:ss
 * NOTE only works for times less than 1 day!
 */
export const convertMsToHHMMSS = (milliseconds: number) => {
  try {
    if (milliseconds) {
      // less than 1 minute, return the string in 0:ss format (with leading single zero!)
      if (milliseconds < SECONDS_PER_MINUTE * MILLISECONDS_PER_SECONDS) {
        return new Date(milliseconds)
          .toISOString()
          .substr(14, 5)
          .replace(/^0/, '');
      }

      // for all else, return the string with no leading zeros (hh:mm:ss, h:mm:ss, mm:ss or m:ss)
      return new Date(milliseconds)
        .toISOString()
        .substr(11, 8)
        .replace(/^[0:]+/, '');
    }

    return '0:00';
  } catch (error) {
    throw new Error(`Error converting ${milliseconds} HHMMSS.`, {
      cause: error,
    });
  }
};

/**
 * converts the given seconds to a duration timestamp in the format hh:mm:ss
 * NOTE only works for times less than 1 day!
 */
export const convertSecondsToHHMMSS = (seconds: number) => {
  return convertMsToHHMMSS(seconds * 1000);
};

export const convertHHMMSStoSeconds = (str?: string) => {
  if (!str) return 0;

  const parts = str.split(':');
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);

  return hours * 3600 + minutes * 60 + seconds;
};

export const ageInHours = (dateString?: string | Date) =>
  dateString
    ? Math.floor(
        (new Date().getTime() - new Date(dateString).getTime()) /
          (1000 * 60 * 60),
      )
    : undefined;

interface IResult {
  [key: string]: any;
}

const isArray = function (obj: unknown): boolean {
  return Array.isArray(obj);
};

const isObject = function (obj: unknown): boolean {
  return obj === Object(obj) && !isArray(obj) && typeof obj !== 'function';
};

const toCamelCase = (str: string) => {
  const regex = /([-_][a-z,0-9])/gi;

  return str.replace(regex, key =>
    key.toUpperCase().replace('-', '').replace('_', ''),
  );
};

const toSnakeCase = (str: string) => {
  const regex = /([A-Z,0-9])/g;

  return (str.match(regex) || [])
    .reduce(
      (current, char) => current.replace(char, '_' + char.toLowerCase()),
      str,
    )
    .substring(str.slice(0, 1).match(regex) ? 1 : 0);
};

export const camelCaseKeys = <T extends IResult>(obj?: unknown): T => {
  if (isObject(obj)) {
    const result: Record<string, unknown> = {};

    Object.entries(obj as object).forEach(([key, value]) => {
      result[toCamelCase(key)] = camelCaseKeys(value);
    });

    return result as T;
  } else if (isArray(obj)) {
    return (obj as Array<unknown>).map(camelCaseKeys) as unknown as T;
  }

  return obj as T;
};

export const snakeCaseKeys = <T extends IResult>(obj?: unknown): T => {
  if (isObject(obj)) {
    const result: Record<string, unknown> = {};

    Object.entries(obj as object).forEach(([key, value]) => {
      result[toSnakeCase(key)] = snakeCaseKeys(value);
    });

    return result as T;
  } else if (isArray(obj)) {
    return (obj as Array<unknown>).map(snakeCaseKeys) as unknown as T;
  }

  return obj as T;
};

export const convertSecondsToMinutes = (seconds: number) =>
  Math.round(seconds / SECONDS_PER_MINUTE);

/** display the file size in simple manner */
export function displaySize(size: number) {
  if (size >= 2 ** 30) {
    return `${Math.floor(size / 2 ** 30)} GB`;
  }
  if (size >= 2 ** 20) {
    return `${Math.floor(size / 2 ** 20)} MB`;
  }
  if (size >= 2 ** 10) {
    return `${Math.floor(size / 2 ** 10)} KB`;
  }
  return `${size} byte${size > 1 ? 's' : ''}`;
}

export const formatNumber = (num: number) => {
  return num.toLocaleString('en-US');
};

export const formatNumberCompact = (num: number) => {
  const compactFormatter = new Intl.NumberFormat('en-US', {
    notation: 'compact',
  });

  return compactFormatter.format(num);
};

export const getSymbolForCurrency = (currencyCode?: string) => {
  const currencyInfo = Object.values(Currency).find(
    c => c.type === currencyCode?.toUpperCase(),
  );
  return currencyInfo ? currencyInfo.symbol : Currency.CAN.symbol;
};

export const capitalizeFirstLetter = (str: string) => {
  const firstChar = str.charAt(0);
  if (/[a-z]/.test(firstChar)) {
    return firstChar.toUpperCase() + str.slice(1);
  }
  return str;
};
